import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createTickets } from "@/queries/tickets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronLeft,
  Cpu,
  DollarSign,
  Loader2,
  MessageCircle,
  Send,
  Star,
  ThumbsUp,
  User,
} from "lucide-react";
import { TicketCategory, type Ticket } from "@/types";
import { Link } from "@tanstack/react-router";
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@radix-ui/react-select";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const ticketOptions = [
  { value: "technical", label: TicketCategory.technical, icon: Cpu },
  { value: "billing", label: TicketCategory.billing, icon: DollarSign },
  { value: "account", label: TicketCategory.account, icon: User },
  {
    value: "feature_request",
    label: TicketCategory.feature_request,
    icon: Star,
  },
  { value: "general", label: TicketCategory.general, icon: MessageCircle },
  { value: "feedback", label: TicketCategory.feedback, icon: ThumbsUp },
];

const schema = z.object({
  title: z.string(),
  message: z.string({
    required_error: "وارد کردن پیام الزامی می باشد",
  }),
  category: z.enum([
    "technical",
    "billing",
    "account",
    "feature_request",
    "general",
    "feedback",
  ]),
});

export const Route = createFileRoute("/support/tickets/create/")({
  component: CreateTicket,
});

function CreateTicket() {
  const navigate = useNavigate();

  const makeTicketMutation = useMutation({
    mutationFn: (variables: Ticket) => createTickets(variables),
    mutationKey: ["make", "new", "ticket"],
    onSuccess() {
      toast.success("تیکت با موفقیت ارسال شد");
      navigate({ to: "/support/tickets" });
    },
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (values: z.infer<typeof schema>) => {
    makeTicketMutation.mutate(values);
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MessageCircle className="size-5" />
            </div>
            <CardTitle className="text-lg">ایجاد تیکت جدید</CardTitle>
          </div>

          <Link to="/support/tickets">
            <Button variant="outline" className="rounded-full">
              بازگشت
              <ChevronLeft className="mr-1 size-4" />
            </Button>
          </Link>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <FormField
                name="category"
                control={form.control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <FormLabel>دسته بندی</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(e) => {
                          field.onChange({ target: { value: e } });
                        }}
                      >
                        <SelectTrigger className="w-full rounded-xl">
                          <SelectValue
                            placeholder="دسته بندی را انتخاب کنید"
                            {...field}
                          />
                        </SelectTrigger>

                        <SelectContent>
                          {ticketOptions.map((category) => (
                            <SelectItem
                              value={category.value}
                              key={category.value}
                            >
                              {category.icon && (
                                <category.icon className="ml-2 size-4" />
                              )}
                              {category.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </div>
                )}
              ></FormField>

              <FormField
                name="title"
                control={form.control}
                render={({ field }: any) => (
                  <div className="flex flex-col gap-2">
                    <FormLabel>عنوان</FormLabel>
                    <FormControl>
                      <Input name="title" className="rounded-xl" {...field} />
                    </FormControl>
                    <FormMessage />
                  </div>
                )}
              ></FormField>

              <FormField
                name="message"
                control={form.control}
                render={({ field }: any) => (
                  <div className="flex flex-col gap-2">
                    <FormLabel>متن پیام</FormLabel>
                    <FormControl>
                      <Textarea
                        name="message"
                        rows={5}
                        className="resize-none rounded-xl"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                )}
              ></FormField>

              <Button
                type="submit"
                className="mt-2 w-full rounded-full"
                disabled={
                  makeTicketMutation.isPending || !form.formState.isValid
                }
              >
                {makeTicketMutation.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    ارسال تیکت
                    <Send className="mr-2 size-4" />
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
